import { Didomi } from '@didomi/react-native';
import { Linking, StyleSheet, TouchableOpacity, View } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../app/hooks';
import { CustomIcon } from '../components/CustomIcon';
import CustomText from '../components/CustomText';
import { ScreenLayout } from '../components/ScreenLayout';
import { COLORS, isTablet } from '../constants';
import {
  selectAppConfig,
  selectPrivacyPolicyUrl,
  selectRadioTab,
  selectTermsOfServiceUrl,
} from '../features/appconfig/store/selectors';
import { useAdjustedFontSize } from '../hooks/useAdjustedFontSize';
import i18n from '../localization/i18n';
import { LeftDrawerNavigatorScreenProps } from '../navigation/LeftDrawerNavigator';

export default function SettingsScreen({
  navigation,
}: LeftDrawerNavigatorScreenProps<'Settings'>) {
  const appConfig = useAppSelector(selectAppConfig);
  const radioTab = useAppSelector(selectRadioTab);
  const privacyPolicyUrl = useAppSelector(selectPrivacyPolicyUrl);
  const termsOfServiceUrl = useAppSelector(selectTermsOfServiceUrl);

  const cmpActive = appConfig.cmpAvailable && appConfig.cmpOption;
  const headerTextColor = radioTab?.headerTextColor ?? COLORS.WHITE;

  const titleFontSize = useAdjustedFontSize(mvs(25));
  const titleLineHeight = useAdjustedFontSize(mvs(30));
  const rowFontSize = useAdjustedFontSize(isTablet ? mvs(15) : mvs(16));

  const handleBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('Player');
    }
  };

  const handleManageConsent = async () => {
    await Didomi.showPreferences();
  };

  const handleOpenUrl = (url: string) => {
    Linking.openURL(url);
  };

  const renderRow = (label: string, onPress: () => void, testID: string) => (
    <TouchableOpacity style={styles.row} onPress={onPress} testID={testID}>
      <CustomText style={[styles.rowLabel, { fontSize: rowFontSize }]} weight="Regular">
        {label}
      </CustomText>
      <CustomIcon name="chevron_right" size={mvs(16)} color="#9C9C9C" />
    </TouchableOpacity>
  );

  return (
    <ScreenLayout
      noMiniPlayer
      headerContents={
        <View style={styles.header}>
          <TouchableOpacity
            onPress={handleBack}
            activeOpacity={0.7}
            accessibilityLabel={i18n.t('settings.close')}
          >
            <View style={styles.backIconContainer}>
              <CustomIcon name="back" size={mvs(24)} color={headerTextColor} />
            </View>
          </TouchableOpacity>
          <CustomText
            style={[
              styles.title,
              {
                color: headerTextColor,
                fontSize: titleFontSize,
                lineHeight: titleLineHeight,
              },
            ]}
            weight="Bold"
          >
            {i18n.t('settings.title')}
          </CustomText>
          {/* spacer to balance the back button so the title is centered on tablet */}
          {isTablet && <View style={styles.backIconContainer} />}
        </View>
      }
    >
      <View style={styles.contentArea}>
        <View style={styles.contentWrapper}>
          <View style={styles.content}>
            {cmpActive &&
              renderRow(
                i18n.t('settings.managePersonalData'),
                handleManageConsent,
                'SettingsManagePersonalData',
              )}
            {!!privacyPolicyUrl &&
              renderRow(
                i18n.t('settings.privacyPolicy'),
                () => handleOpenUrl(privacyPolicyUrl),
                'SettingsPrivacyPolicy',
              )}
            {!!termsOfServiceUrl &&
              renderRow(
                i18n.t('settings.termsOfService'),
                () => handleOpenUrl(termsOfServiceUrl),
                'SettingsTermsOfService',
              )}
          </View>
        </View>
      </View>
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: mvs(8),
  },
  backIconContainer: {
    width: mvs(24),
    height: mvs(24),
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    flex: 1,
    textAlign: isTablet ? 'center' : 'left',
  },
  content: {
    flex: 1,
    paddingHorizontal: mvs(16),
    paddingTop: mvs(8),
    width: '100%',
    alignSelf: 'center',
  },
  contentArea: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  contentWrapper: {
    flex: 1,
    width: '100%',
    maxWidth: isTablet ? mvs(600) : undefined,
    alignSelf: 'center',
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: mvs(8),
    borderTopRightRadius: mvs(8),
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: mvs(16),
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#E0E0E0',
  },
  rowLabel: {
    color: '#000',
    flex: 1,
    paddingRight: mvs(12),
  },
});
